import React, { useState } from "react";
import { notification } from "antd";
import styled from "styled-components";
import API_URL from "../../config";

const axios = require("axios").default;

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 2, currentYear - 1, currentYear];

const PKG_COLORS = [
  "#ae094d", "#1890ff", "#52c41a", "#fa8c16",
  "#722ed1", "#13c2c2", "#f5222d", "#a0d911",
  "#eb2f96", "#faad14", "#2f54eb", "#389e0d",
];

/* ── Styled ── */
const PageWrap = styled.div` padding: 24px; `;

const FilterRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 28px; flex-wrap: wrap;
`;

const Label = styled.label` font-size: 13px; font-weight: 500; color: #555; margin-right: 4px; `;

const YearButton = styled.button`
  padding: 7px 22px;
  font-size: 14px;
  border-radius: 6px;
  border: 1.5px solid ${(p) => (p.active ? "#ae094d" : "#d9d9d9")};
  background: ${(p) => (p.active ? "#ae094d" : "#fff")};
  color: ${(p) => (p.active ? "#fff" : "#555")};
  cursor: pointer;
  font-weight: ${(p) => (p.active ? 700 : 400)};
  &:hover { border-color: #ae094d; color: ${(p) => (p.active ? "#fff" : "#ae094d")}; }
`;

const ChartsWrap = styled.div`
  display: flex; gap: 40px; flex-wrap: wrap; align-items: flex-start;
`;

const ChartBox = styled.div`
  flex: 1; min-width: 300px;
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 20px 24px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const ChartBoxFull = styled.div`
  width: 100%;
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 20px 24px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  margin-top: 32px;
`;

const ChartTitle = styled.div`
  font-size: 14px; font-weight: 600; color: #333; margin-bottom: 20px;
`;

const BarsArea = styled.div`
  display: flex; align-items: flex-end; gap: 8px; height: 260px;
`;

const StackedBarsArea = styled.div`
  display: flex; align-items: flex-end; gap: 8px; height: 260px;
`;

const BarGroup = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px; flex: 1;
`;

const BarOuter = styled.div`
  width: 100%; max-width: 44px; height: 200px;
  display: flex; align-items: flex-end;
  background: #f5f5f5; border-radius: 4px 4px 0 0; overflow: hidden;
`;

const StackedBarOuter = styled.div`
  width: 100%; max-width: 44px; height: 200px;
  position: relative;
  background: #f5f5f5; border-radius: 4px 4px 0 0; overflow: hidden;
`;

const BarInner = styled.div`
  width: 100%;
  height: ${(p) => p.pct}%;
  background: ${(p) => p.color || "#ae094d"};
  border-radius: 4px 4px 0 0;
  transition: height 0.5s ease;
  display: flex; align-items: flex-start; justify-content: center;
`;

const BarVal = styled.span`
  font-size: 9px; font-weight: 700; color: #fff; margin-top: 4px; text-align: center;
`;

const BarMonth = styled.div`
  font-size: 10px; color: #777; text-align: center;
`;

const BarBottom = styled.div`
  font-size: 9px; color: ${(p) => p.color || "#ae094d"}; font-weight: 600; text-align: center;
`;

const SummaryRow = styled.div`
  display: flex; gap: 16px; margin-bottom: 28px; flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 14px 28px; text-align: center; min-width: 160px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  .value { font-size: 26px; font-weight: 700; color: ${(p) => p.color || "#ae094d"}; }
  .title { font-size: 12px; color: #888; margin-top: 4px; }
`;

const Legend = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px 20px; margin-top: 20px;
  padding-top: 16px; border-top: 1px solid #f0f0f0;
`;

const LegendItem = styled.div`
  display: flex; align-items: center; gap: 6px; font-size: 11px; color: #444;
`;

const LegendDot = styled.div`
  width: 10px; height: 10px; border-radius: 2px;
  background: ${(p) => p.color}; flex-shrink: 0;
`;

/* ── Component ── */
const MonthlyReport = () => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async (year) => {
    setSelectedYear(year);
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API_URL}monthly-report/report`, {
        params: { year },
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      notification.error({ message: "Failed to load report." });
    } finally {
      setLoading(false);
    }
  };

  const totalCount = data ? data.monthlyData.reduce((s, r) => s + r.count, 0) : 0;
  const totalTL = data ? data.monthlyData.reduce((s, r) => s + r.total_tl, 0) : 0;
  const maxCount = data ? Math.max(...data.monthlyData.map((r) => r.count), 1) : 1;
  const maxTL = data ? Math.max(...data.monthlyData.map((r) => r.total_tl), 1) : 1;

  /* Paket dağılımı hesapları */
  const buildPackageChart = () => {
    if (!data || !data.packageData) return null;

    // Benzersiz paketler, toplam satışa göre sıralı
    const pkgTotals = {};
    const pkgNames = {};
    data.packageData.forEach((r) => {
      pkgTotals[r.plan_id] = (pkgTotals[r.plan_id] || 0) + r.count;
      pkgNames[r.plan_id] = r.package_name;
    });
    const packages = Object.keys(pkgTotals)
      .map((id) => ({ plan_id: parseInt(id), package_name: pkgNames[id] }))
      .sort((a, b) => pkgTotals[b.plan_id] - pkgTotals[a.plan_id]);

    // Ay → paket → count haritası
    const monthMap = {};
    data.packageData.forEach((r) => {
      if (!monthMap[r.month]) monthMap[r.month] = {};
      monthMap[r.month][r.plan_id] = r.count;
    });

    const maxMonthTotal = Math.max(...data.monthlyData.map((r) => r.count), 1);

    return (
      <ChartBoxFull>
        <ChartTitle>Package mix by month — {selectedYear}</ChartTitle>
        <StackedBarsArea>
          {data.monthlyData.map((row) => {
            const pkgMap = monthMap[row.month] || {};
            return (
              <BarGroup key={row.month}>
                <StackedBarOuter>
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column-reverse",
                    }}
                  >
                    {packages.map((pkg, i) => {
                      const count = pkgMap[pkg.plan_id] || 0;
                      if (!count) return null;
                      const heightPx =
                        maxMonthTotal > 0 ? (count / maxMonthTotal) * 200 : 0;
                      return (
                        <div
                          key={pkg.plan_id}
                          title={`${pkg.package_name}: ${count}`}
                          style={{
                            width: "100%",
                            height: `${heightPx}px`,
                            background: PKG_COLORS[i % PKG_COLORS.length],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {heightPx >= 16 && (
                            <span
                              style={{
                                fontSize: 8,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              {count}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </StackedBarOuter>
                <BarMonth>{row.name.slice(0, 3)}</BarMonth>
                <BarBottom color="#555">
                  {row.count > 0 ? row.count : "—"}
                </BarBottom>
              </BarGroup>
            );
          })}
        </StackedBarsArea>

        <Legend>
          {packages.map((pkg, i) => (
            <LegendItem key={pkg.plan_id}>
              <LegendDot color={PKG_COLORS[i % PKG_COLORS.length]} />
              {pkg.package_name} ({pkgTotals[pkg.plan_id]})
            </LegendItem>
          ))}
        </Legend>
      </ChartBoxFull>
    );
  };

  return (
    <PageWrap>
      <h2 style={{ marginBottom: 20 }}>Monthly subscription report</h2>

      <FilterRow>
        <Label>Year:</Label>
        {YEARS.map((y) => (
          <YearButton
            key={y}
            active={selectedYear === y}
            onClick={() => fetchReport(y)}
            disabled={loading}
          >
            {y}
          </YearButton>
        ))}
      </FilterRow>

      {loading && <div style={{ color: "#aaa", padding: "20px 0" }}>Loading...</div>}

      {data && !loading && (
        <>
          <SummaryRow>
            <SummaryCard>
              <div className="value">{totalCount}</div>
              <div className="title">{selectedYear} total subscriptions</div>
            </SummaryCard>
            <SummaryCard color="#52c41a">
              <div className="value">{totalTL.toLocaleString("tr-TR")} ₺</div>
              <div className="title">{selectedYear} total revenue</div>
            </SummaryCard>
          </SummaryRow>

          <ChartsWrap>
            {/* Aylık Satış Sayısı */}
            <ChartBox>
              <ChartTitle>Monthly sales count — {selectedYear}</ChartTitle>
              <BarsArea>
                {data.monthlyData.map((row) => {
                  const pct = maxCount > 0 ? Math.round((row.count / maxCount) * 100) : 0;
                  return (
                    <BarGroup key={row.month}>
                      <BarOuter>
                        <BarInner pct={pct} color="#ae094d">
                          {pct >= 15 && <BarVal>{row.count}</BarVal>}
                        </BarInner>
                      </BarOuter>
                      <BarMonth>{row.name.slice(0, 3)}</BarMonth>
                      <BarBottom color="#ae094d">{row.count}</BarBottom>
                    </BarGroup>
                  );
                })}
              </BarsArea>
            </ChartBox>

            {/* Aylık Gelir */}
            <ChartBox>
              <ChartTitle>Monthly revenue (TRY) — {selectedYear}</ChartTitle>
              <BarsArea>
                {data.monthlyData.map((row) => {
                  const pct = maxTL > 0 ? Math.round((row.total_tl / maxTL) * 100) : 0;
                  return (
                    <BarGroup key={row.month}>
                      <BarOuter>
                        <BarInner pct={pct} color="#52c41a">
                          {pct >= 15 && <BarVal>{Math.round(row.total_tl / 1000)}K</BarVal>}
                        </BarInner>
                      </BarOuter>
                      <BarMonth>{row.name.slice(0, 3)}</BarMonth>
                      <BarBottom color="#52c41a">
                        {row.total_tl > 0 ? `${Math.round(row.total_tl).toLocaleString("tr-TR")}₺` : "—"}
                      </BarBottom>
                    </BarGroup>
                  );
                })}
              </BarsArea>
            </ChartBox>
          </ChartsWrap>

          {/* Paket dağılımı stacked bar */}
          {buildPackageChart()}
        </>
      )}
    </PageWrap>
  );
};

export default MonthlyReport;
